import { useState } from 'react';
import {
  AccountModel,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  NgoaiTe,
  NhanVien,
  PhuongThucGiaoHang,
  PhuongTienVanChuyen,
  QuyenChungTu
} from '@/types/schemas';

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  nhanVien: BanHangRecord | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  ngoaiTe: NgoaiTe | null;
  donViCoSo: DonViCoSo | null;
  soChungTuHienTai: string;

  // Other information
  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongTienGiaoHang: PhuongThucGiaoHang | null;
  coQuanThue: KhachHang | null;
  dienGiai: string;

  // Checkbox states
  pt_tao_yn: boolean;
  transfer_yn: boolean;
  ty_gia: number;

  ma_httt: string;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang) => void;
  setNhanVien: (nhanVien: BanHangRecord) => void;
  setTaiKhoan: (taiKhoan: AccountModel | null) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setNgoaiTe: (ngoaiTe: NgoaiTe) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;
  setSoChungTuHienTai: (soChungTuHienTai: string) => void;
  setMaHttt: (maHt: string) => void;

  setDiaChi: (diaChi: DiaChi) => void;
  setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => void;
  setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => void;
  setCoQuanThue: (coQuanThue: KhachHang) => void;
  setDienGiai: (dienGiai: string) => void;

  // Checkbox setters
  setPtTaoYn: (ptTaoYn: boolean) => void;
  setTransferYn: (transferYn: boolean) => void;
  setTyGia: (tyGia: number) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  nhanVien: null,
  taiKhoan: null,
  hanThanhToan: null,

  // Document information
  quyenChungTu: null,
  ngoaiTe: null,
  donViCoSo: null,
  soChungTuHienTai: '',

  // Other information
  diaChi: null,
  phuongTienVanChuyen: null,
  phuongTienGiaoHang: null,
  coQuanThue: null,
  dienGiai: '',

  // Checkbox states
  pt_tao_yn: false,
  transfer_yn: false,
  ty_gia: 1,
  ma_httt: 'TMB'
};

function transformInitialData(initialData?: BanHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};
  const ma_kh_data = {
    ...initialData.ma_kh_data,
    email: initialData.e_mail,
    address: initialData.dia_chi,
    contact_person: initialData.ong_ba,
    tax_code: initialData.ma_so_thue
  };
  return {
    // Customer information
    khachHang: ma_kh_data || null,
    nhanVien: initialData.ma_nvbh_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.han_tt_data || null,
    dienGiai: initialData.dien_giai || '',
    soChungTuHienTai: initialData.so_ct || '',
    ma_httt: initialData.ma_httt || null,

    // Document information
    quyenChungTu: initialData.so_ct_data || null,
    ngoaiTe: initialData.ma_nt_data || null,
    donViCoSo: initialData.unit_id_data || null,

    // Other information
    diaChi: initialData.ma_dc_data || null,
    phuongTienVanChuyen: initialData.ma_ptvc_data || null,
    phuongTienGiaoHang: initialData.ma_ptgh_data || null,
    coQuanThue: initialData.ma_kh9_data || null,

    // Checkbox states
    pt_tao_yn: initialData.pt_tao_yn || false,
    transfer_yn: initialData.transfer_yn || true,
    ty_gia: initialData.ty_gia || 1
  };
}

export function useFormFieldState(initialData?: BanHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhanVien: (nhanVien: BanHangRecord) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setMaHttt: (ma_httt: string) => {
      setState(prev => ({
        ...prev,
        ma_httt
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel | null) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setNgoaiTe: (ngoaiTe: NgoaiTe) => {
      setState(prev => ({
        ...prev,
        ngoaiTe
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setSoChungTuHienTai: (soChungTuHienTai: string) => {
      setState(prev => ({
        ...prev,
        soChungTuHienTai
      }));
    },

    setDiaChi: (diaChi: DiaChi) => {
      setState(prev => ({
        ...prev,
        diaChi
      }));
    },

    setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => {
      setState(prev => ({
        ...prev,
        phuongTienVanChuyen
      }));
    },

    setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => {
      setState(prev => ({
        ...prev,
        phuongTienGiaoHang
      }));
    },

    setCoQuanThue: (coQuanThue: KhachHang) => {
      setState(prev => ({
        ...prev,
        coQuanThue
      }));
    },

    setDienGiai: (dienGiai: string) => {
      setState(prev => ({ ...prev, dienGiai }));
    },

    setPtTaoYn: (ptTaoYn: boolean) => {
      setState(prev => ({ ...prev, pt_tao_yn: ptTaoYn }));
    },

    setTransferYn: (transferYn: boolean) => {
      setState(prev => ({ ...prev, transfer_yn: transferYn }));
    },

    setTyGia: (tyGia: number) => {
      setState(prev => ({ ...prev, ty_gia: tyGia }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
